import * as React from 'react';
import { FC, useState } from 'react';
import { format } from 'date-fns';
import { Alert, Button, ButtonsContainer, typeCompact14Regular, Window, WindowBody } from '@traceair/tds';
import { trackAction } from '../../../../../../libs/analytics/ActionTracking';
import { ScanDate, Site } from '../../../../../../types';
import { classes, style } from 'typestyle';
import { FlightDatePickerCancelledError } from './FlightDatePickerCancelledError';
import FlightDatePicker from '../FlightDatePicker';
import { dateToScheduleFormat } from '../../../helpers/date';
import { makeSafeIsoDateString } from '../../../../../../libs/doneDate';
import { ConfirmDateBounds, resolveConfirmDateBounds } from './confirmDateBounds';

/**
 * Which trigger opened the window. The two are independent: `future` covers a wrong date that agrees
 * with the schedule, i.e. a flight that has not happened yet, which `offSchedule` cannot see.
 */
export type DateDiscrepancyReason = 'offSchedule' | 'future';

type FlightDateConfirmationWindowProps = {
  showWindow: (window: React.ReactNode) => void;
  hideWindow: (window: React.ReactNode) => void;
  photoDate: ScanDate;
  scheduledDate: ScanDate;
  today: ScanDate;
  daysDiff: number;
  reason: DateDiscrepancyReason;
  /** Whether the photo date came from a GPS timestamp. Selects which of the two windows is shown. */
  precise: boolean;
  site: Site;
};

const bodyStyle = classes(typeCompact14Regular, style({ padding: 12, textAlign: 'left' }));
const pickerStyle = style({ padding: '0 12px 12px', width: 220, textAlign: 'left' });

// The format the picker field renders, so dates in the message and in the picker below it match.
const readable = (date: ScanDate) => format(new Date(makeSafeIsoDateString(date)), 'd LLL yyyy');

/** Heading for the GPS window. The suspicion lives here so the body can stay to fact + action. */
const gpsTitle = (reason: DateDiscrepancyReason) => (reason === 'future' ? 'Date is in the future' : 'Wrong folder?');

/** Message for the GPS window: the date is not in question, only which folder was picked. */
const gpsMessage = ({ reason, photoDate, scheduledDate, daysDiff }: FlightDateConfirmationWindowProps) => {
  const gap =
    reason === 'future'
      ? `${readable(photoDate)}, after today`
      : `${readable(photoDate)} — ${daysDiff} ${daysDiff === 1 ? 'day' : 'days'} off the scheduled ${readable(
          scheduledDate,
        )}`;

  return `These photos are dated ${gap}. Continue and the flight date updates to match.`;
};

/**
 * Message for the no-GPS window. Says "the drone" rather than naming clocks or metadata, since that is
 * the part of the setup the pilot can check before the next flight.
 */
const noGpsMessage = ({ reason, photoDate, scheduledDate }: FlightDateConfirmationWindowProps) => {
  const conflict =
    reason === 'future'
      ? `The drone says ${readable(photoDate)}, which is after today`
      : `The drone says ${readable(photoDate)}, the schedule says ${readable(scheduledDate)}`;

  return `${conflict}. Drone clocks can be wrong — set the date you actually flew.`;
};

/**
 * Shows one of two windows for a flight date that does not match the schedule:
 *
 *  - GPS date — a warning with no date input, since the timestamp is not in question.
 *  - no GPS date — a required date, since it came from the drone's clock. Always the case for panoramas.
 *
 * Resolves with the date the pilot stated, or `undefined` for the warning window. Rejects with
 * {@link FlightDatePickerCancelledError} if they back out; the upload itself is never refused.
 */
export default function confirmFlightDate(props: FlightDateConfirmationWindowProps): Promise<ScanDate | undefined> {
  const { showWindow, hideWindow, scheduledDate, today, precise, site } = props;

  return new Promise((resolve, reject) => {
    const handleCancel = () => {
      hideWindow(modal);
      reject(new FlightDatePickerCancelledError());
    };

    const handleProceed = (confirmedDate?: ScanDate) => {
      trackAction('Confirm date upload button click', null);
      hideWindow(modal);
      resolve(confirmedDate);
    };

    const modal = precise ? (
      <Window title={gpsTitle(props.reason)} onClose={handleCancel} subTitle={site.name}>
        <WindowBody>
          <div className={bodyStyle}>
            <Alert type="attention" title={gpsMessage(props)} />
          </div>
          <ButtonsContainer>
            <Button style="secondary" onClick={handleCancel} text="Cancel" />
            <Button style="primary" onClick={() => handleProceed()} text="Continue" className="confirm-button" />
          </ButtonsContainer>
        </WindowBody>
      </Window>
    ) : (
      <Window title="When did you fly?" onClose={handleCancel} subTitle={site.name}>
        <ConfirmDateBody
          message={noGpsMessage(props)}
          bounds={resolveConfirmDateBounds(scheduledDate, today)}
          onCancel={handleCancel}
          onConfirm={handleProceed}
        />
      </Window>
    );

    showWindow(modal);
  });
}

type ConfirmDateBodyProps = {
  message: string;
  bounds: ConfirmDateBounds;
  onCancel: () => void;
  onConfirm: (date: ScanDate) => void;
};

const ConfirmDateBody: FC<ConfirmDateBodyProps> = ({ message, bounds, onCancel, onConfirm }) => {
  const [date, setDate] = useState<Date>(new Date(makeSafeIsoDateString(bounds.initial)));

  return (
    <WindowBody>
      <div className={bodyStyle}>
        <Alert type="attention" title={message} />
      </div>
      <div className={pickerStyle}>
        <FlightDatePicker
          date={date}
          minDate={new Date(makeSafeIsoDateString(bounds.min))}
          maxDate={new Date(makeSafeIsoDateString(bounds.max))}
          onChange={(d) => d && setDate(d)}
          placeholder="Flight date"
        />
      </div>
      <ButtonsContainer>
        <Button style="secondary" onClick={onCancel} text="Cancel" />
        <Button
          style="primary"
          onClick={() => onConfirm(dateToScheduleFormat(date))}
          text="Confirm"
          className="confirm-button"
        />
      </ButtonsContainer>
    </WindowBody>
  );
};
